import * as Sentry from "@sentry/react";
import React from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AdminContainer, AdminTitle } from "../../../components/Admin";
import Spacer from "../../../components/Spacer";
import WorkflowsTab from "./WorkflowsTab";

import { useRbacPrimaryRolePermissions } from "../../../api/hooks";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

import { rbacPermissionsQuery } from "../../../api/queries";
import { SimplifiedQuery, dataLoader } from "../../../api/queries/dataLoader";

import { getIsUserSuperuser } from "../../../selectors/app";
import { getCompanySettingsContext, settingsHaveBeenFetched } from "../../../selectors/settings";

interface WorkflowsAdminProps {
	tab: "workflows";
	requireSuperuser?: boolean;
}

export const workflowsLoader = () => {
	const queries: Array<() => SimplifiedQuery> = [() => rbacPermissionsQuery("workflows")];

	return dataLoader([queries]);
};

const WorkflowsAdmin = (props: WorkflowsAdminProps) => {
	const { tab, requireSuperuser } = props;
	const {
		value: workflowsFeatureVisible,
		query: { isLoading: isLoadingWorkflowsFF },
	} = useFeatureFlagQuery("ff-is-workflows-enabled", true);
	const { data: rbacPermissions, isLoading: isRbacLoading } = useRbacPrimaryRolePermissions("workflows");

	const isUserSuperuser = useSelector(getIsUserSuperuser);
	const companySettings = useSelector(getCompanySettingsContext);
	const haveSettingsBeenFetched = useSelector(settingsHaveBeenFetched);
	const workflowsEnabled = companySettings.getIn(["workflows", "state"], "disabled") === "enabled";

	const isLoadingAny = isLoadingWorkflowsFF || isRbacLoading || !haveSettingsBeenFetched;

	const hasViewPermission = rbacPermissions?.includes("workflows:viewAdmin");
	const featureAvailable = isUserSuperuser || (workflowsFeatureVisible && workflowsEnabled);

	const renderInfoBox = (heading: string, message: string) => (
		<div>
			<AdminTitle includeSpacer={false}>{heading}</AdminTitle>
			<div className="bg-py-primary-1 flex w-[400px] gap-1.5 rounded-sm p-5">
				<FontAwesomeIcon icon={faInfoCircle} className="text-py-primary-7 text-lg" />
				<div className="font-semibold">{message}</div>
			</div>
		</div>
	);

	const renderGatedContent = (): React.ReactNode => {
		if (isLoadingAny) return null;
		if (requireSuperuser && !isUserSuperuser) {
			return <Navigate to="/administration/workflows/" replace />;
		}
		if (!featureAvailable) {
			return renderInfoBox("Workflows", "This feature is not enabled for your organization.");
		}
		if (!hasViewPermission) {
			return renderInfoBox("Workflows", "Missing required permissions.");
		}
		switch (tab) {
			case "workflows":
				return (
					<>
						<Spacer height={4} />
						<WorkflowsTab />
					</>
				);
		}
	};

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			<AdminContainer>{renderGatedContent()}</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default WorkflowsAdmin;
